/* eslint max-lines: "off" */

// ****************************************************************************************************************
// NOTE: While we convert from backbone to react, keep the backbone colors file in sync with the react colors file.
// ****************************************************************************************************************

/* eslint-disable key-spacing */

// PALETTE COLORS:

import Brand from 'helpers/brand'

const rthChart = {
  blue            : '#1F729D',
  brown           : '#8C5402',
  magenta         : '#AF18B8',
  teal            : '#00DDC6',
  green           : '#00870E',
  yellow          : '#C3AB1E',
  lavenderBlue    : '#c5cbff',
  purple          : '#6F27DB',
  purpleLight     : '#a993cd',
  purpleLighter   : '#d9b0fd',
  purpleLightest  : '#c5cbff',
  paleRed         : '#E16600',
  darkRed         : '#450000',
  red             : '#b62e00'
}

const consumerRTHChart = {
  cool:               '#6BBEEA',
  coolStage2:         '#1F729D',
  heat:               '#FC6C68',
  heatStage2:         '#BC1C10',
  heatStage3:         '#E68B02',
  heatAux:            '#FF9DA6',
  heatAuxStage2:      '#FC6C68',
  heatAuxStage3:      '#BC1C10',
  hoverBox:           '#F0F0F0',
  hoverBoxBackground: '#F0F0F0',
  chartBackground:    '#F3F7F8'
}

const residential = {
  black           : '#000000',

  dimGray         : '#707070',
  dimGrayLight    : '#A6A6A6',
  dimGrayLighter  : '#F2F2F2',
  dimGrayLightest : '#E3E0DC',
  neutralGray     : '#C3C3C3',

  greyDarkest     : '#4D4D4D',
  greyDarker      : '#767676',
  greyDark        : '#939598',
  grey            : '#AAAAAA',
  greyLight       : '#D9D9D9',
  greyLighter     : '#F5F5F5',
  greyLightest    : '#F6F6F6',
  dimGrey         : '#9E9D9D',
  cloudGray       : '#E4EAEB',
  white           : '#FFFFFF',

  purpleDarker    : '#7332c1',
  purpleDark      : '#3f51b5',
  purple          : '#b94ec6',
  dimPurple       : '#A48EC2',

  redDarker       : '#FF0000',
  redDark         : '#9A1920',
  red             : '#CD3529',
  redLight        : '#D05147',
  redLighter      : '#C78588',
  redLightest     : '#FFD4D4',
  redTranslucent  : 'rgba(205,53,41,0.5)',
  redPink         : '#FFAFAF',
  dimRed          : '#D47068',

  orange          : '#DF661E',

  yellowOrange     : '#FFAE42',
  yellowDarker     : '#ffe300',
  yellowDark       : '#f0c33b',
  yellow           : '#ffd119',
  yellowLight      : '#ffffa6',
  yellowLighter    : '#fbf4e8',
  yellowLightest   : '#fdf8ef',
  yellowTranslucent: 'rgba(255,209,25,0.5)',
  dimYellow        : '#F9E097',

  greenLight      : '#92C04F',
  greenLighter    : '#9DC661',
  green           : '#719C4D',
  greenDark       : '#427438',
  greenBlue       : '#7fffd4',
  dimGreen        : '#B7C69B',

  bluegreen       : '#00FFFF',
  cornflowerBlue  : '#6495ED',

  blueDarkest     : '#3E4DA1',
  blueDarker      : '#06437B',
  blueDark        : '#0B549A',
  blue            : '#2978C2',
  blueLight       : '#81AED8',
  blueLighter     : '#B5CAE3',
  blueLightest    : '#EDF5F9',
  blueTranslucent : '#CEE2FF',

  translucent     : 'rgba(0,0,0,0.45)'
}

const commercial = {
  black           : '#000000',
  grayDarker      : '#333333',
  grayDark        : '#666666',
  gray            : '#818181',
  grayLight       : '#BABBBD',
  grayLightest    : '#F0EEEC',
  white           : '#FFFFFF',

  greenDarker     : '#446C26',
  green           : '#5A9032',
  greenLight      : '#639C38',
  greenLighter    : '#9CBC84',

  grayBlue        : '#3E515F',
  blueDark        : '#003270',
  blue            : '#335B89',
  blueLight       : '#7297C1',
  blueLighter     : '#3B64A5'
}

// COMPONENT COLORS:

const loadComponentColors = () => {
  return {
    executableFocusOutline          : residential.blueDark,
    nonExecutableFocusOutline       : residential.greyDark,

    alertFormPreviewBackground      : residential.greyLightest,
    ancillaryInfoHeaderBackground   : residential.greyLighter,
    background                      : residential.greyLightest,
    alternatingRowBackground        : residential.greyLightest,
    backgroundHover                 : residential.greyLighter,
    border                          : residential.greyLight,
    customerFilterPrefixColumn      : residential.greyLight,
    devicePanelHeader               : residential.greyDark,
    divider                         : residential.greyLighter,
    draftAlert                      : residential.greyDarker,
    draftAlertHover                 : residential.greyDark,
    disabled                        : residential.greyLighter,

    danger                          : residential.red,
    dangerText                      : residential.white,

    featureTourInfoBackground       : residential.blueLightest,
    featureTourWarningBackground    : residential.yellowLightest,

    featureToursActiveBackground    : residential.white,
    featureToursPublishedBackground : residential.greyLight,
    featureToursDisabledBackground  : residential.grey,
    featureToursSiteLink            : residential.blueDarkest,
    featureToursHeading             : residential.blue,

    mainContentBorder               : residential.greyLighter,
    migrateDealerSpinner            : residential.blueDark,
    modalOverlay                    : residential.translucent,

    navbarBackground                : Brand.isCommercial() ? commercial.white : residential.white,
    navbarBackgroundHover           : Brand.isCommercial() ? commercial.grayDarker : residential.greyLighter,
    navbarBackgroundHoverBorder     : Brand.isCommercial() ? commercial.grayDarker : residential.blueDark,
    navbarBackgroundActive          : Brand.isCommercial() ? commercial.grayDarker : residential.blue,
    navbarArrowBackground           : Brand.isCommercial() ? commercial.grayDarker : residential.blue,
    navbarArrowBackgroundHover      : Brand.isCommercial() ? commercial.grayDarker : residential.blueDark,
    navbarHoverText                 : residential.blueDark,

    pageSpinner                     : residential.blue,
    paginationBackground            : residential.greyLighter,
    paneBackground                  : residential.white,
    positiveStatus                  : residential.green,
    impersonationBannerBackground   : residential.yellowLight,
    rthChartAxis                    : residential.grey,
    rthChartGridLine                : residential.greyLight,
    rthChartIconHoverBackground     : residential.greyLightest,
    rthLegendControlBackground      : residential.greyLight,
    rthLegendControlBackgroundHover : residential.greyLighter,
    rthLegendBorderHover            : residential.greyDark,
    siteMessageInfoBackground       : residential.blueLightest,
    siteMessageWarningBackground    : residential.yellowLightest,
    tableHeaderBackground           : residential.greyLighter,
    paginationItemActiveBackground  : residential.greyLighter,
    paginationItemHoverBackground   : residential.greyLighter,
    newLabelBackground              : Brand.isCommercial() ? commercial.grayDark : residential.blue,
    disabledAlertNotification       : residential.greyLight,
    deviceMetricsListTitleBackground: residential.greyLight,
    tagBackground                   : residential.greyLighter,
    tagBorder                       : residential.greyLight,

    // borders

    ancillaryInfoPanelBorder        : residential.greyLighter,
    tooltipBorder                   : residential.greyDark,
    siteMessageEnabledStatusBorder  : residential.greyDarker,
    StatusEnabledBorder             : residential.greyDarker,
    menuItemSplit                   : residential.greyLighter,
    menuItemSectionSplit            : residential.greyLight,
    datePickerBorder                : residential.greyLighter,
    datePickerHover                 : residential.greyLighter,
    datePickerLableText             : residential.greyDarkest,
    featureTourActiveBorder         : residential.greyDarker,
    featureTourPublishedBorder      : residential.greyDarker,
    featureTourAddButton            : residential.blue,
    borderGray                      : residential.neutralGray,
    wistiaVideoTableRowBorder       : residential.greyLight,
    columnBorder                    : residential.greyLightest,

    // text

    primaryText                     : residential.greyDarkest,
    secondaryText                   : residential.greyDark,
    checkboxText                    : residential.greyDarker,
    disabledText                    : residential.greyLight,
    ancillaryText                   : residential.grey,
    noDataText                      : residential.grey,
    imageCaptionText                : residential.white,
    imageCaptionBackground          : residential.greyDarker,
    alertSectionNumber              : residential.greyDarker,
    alertSubsection                 : Brand.isCommercial() ? commercial.blueDark : residential.blueDarkest,
    anchor                          : Brand.isCommercial() ? commercial.blueDark : residential.blue,
    anchorHover                     : residential.blueDark,
    customerNameText                : residential.greyDarkest,
    devicePanelHeaderText           : residential.white,
    draftAlertText                  : residential.white,
    errorMessage                    : residential.redDarker,
    filteredByMessage               : residential.greyDark,
    footerText                      : residential.greyDarker,
    emailFormText                   : residential.greyDarker,

    featureToursActiveText          : residential.white,
    featureToursPublishedText       : residential.greyDarker,
    featureToursDisabledText        : residential.grey,
    featureToursUpdatedAt           : residential.greyDarker,

    headerText                      : residential.greyDarkest,
    infoPanelHeaderText             : residential.greyDark,
    navLinkText                     : residential.white,
    navLinkTextNotSelected          : residential.dimGray,
    navMenuTextHover                : residential.greyLight,
    watermarkTitle                  : residential.greyLight,
    watermarkDescription            : residential.greyDark,
    rthChartAxisLabel               : residential.greyDark,
    rthChartDate                    : residential.greyDarkest,
    rthChartInstructions            : residential.greyDark,
    rthLegendControlsLabel          : residential.greyDark,
    titleText                       : residential.greyDarkest,
    tooltipText                     : residential.greyDarker,
    rthLegendControl                : residential.greyDarkest,
    rthLegendTime                   : residential.grey,
    selectedRthLegendControl        : residential.black,
    rthHelpText                     : residential.grey,
    siteMessageStatusEnabledText    : residential.white,
    siteMessageStatusDisabledText   : residential.grey,
    statusDisabledText              : residential.grey,
    statusEnabledText               : residential.white,
    siteMessageUpdatedAt            : residential.greyDarker,
    subNavText                      : residential.greyDarkest,
    paginationItemTextSelected      : residential.greyDarkest,
    customerDirectoryIndex          : residential.black,
    warrantyText                    : residential.black,
    filteredText                    : residential.yellow,
    successText                     : residential.green,
    errorText                       : residential.red,
    panelFieldLabel                 : residential.grey,
    newLabelText                    : residential.white,
    subNavbarActive                 : residential.blue,
    subNavbarActiveHover            : residential.blue,
    tagText                         : residential.greyDarkest,
    deviceTooltipLabelText          : residential.black,
    linkVerificationReportText      : residential.blue,
    homeownerAlertText              : residential.greyDarker,
    premiumText                     : residential.greyDarker,

    // buttons

    primaryButton                   : Brand.isCommercial() ? commercial.green : residential.blue,
    primaryButtonText               : residential.white,
    primaryButtonHover              : Brand.isCommercial() ? commercial.greenLight : residential.blueDark,
    primaryButtonFocus              : Brand.isCommercial() ? commercial.greenLight : residential.blueDarker,
    primaryButtonProcessing         : Brand.isCommercial() ? commercial.greenLight : residential.blueDark,
    primaryButtonSpinner            : residential.white,
    primaryButtonDisabled           : residential.blueLight,

    secondaryButton                 : residential.white,
    secondaryButtonBorder           : residential.blue,
    secondaryButtonText             : residential.greyDarkest,
    secondaryButtonHoverBorder      : residential.greyLightest,
    secondaryButtonHoverText        : residential.blueDark,
    secondaryButtonHover            : residential.greyLightest,
    secondaryButtonProcessing       : residential.greyLightest,
    secondaryButtonSpinner          : residential.greyDark,

    tertiaryButton                  : residential.white,
    tertiaryButtonText              : residential.greyDarkest,
    tertiaryButtonHover             : residential.greyLighter,
    tertiaryButtonProcessing        : residential.greyLighter,
    tertiaryButtonHoverText         : residential.black,

    breadCrumbsText                 : residential.greyDark,
    breadCrumbsBackgroundHover      : residential.greyLighter,
    breadCrumbsTextHover            : residential.black,

    cancelButtonText                : residential.blue,
    cancelButtonBorder              : residential.blue,
    cancelButtonHoverText           : residential.blueDark,
    cancelButtonHoverBorder         : residential.blueDark,

    destructiveButton               : residential.red,
    destructiveButtonText           : residential.white,
    destructiveButtonHover          : residential.redLight,
    destructiveButtonProcessing     : residential.redLight,

    semiDestructiveButton           : residential.red,
    semiDestructiveButtonBackground : residential.white,
    semiDestructiveButtonHover      : residential.redLightest,

    inconspicuousButtonText         : residential.greyLight,

    searchButton                    : Brand.isCommercial() ? commercial.grayDark : residential.blue,
    searchButtonHover               : Brand.isCommercial() ? commercial.grayDarker : residential.blueDark,

    systemStatusButtonBorder        : residential.greyLighter,
    systemStatusButtonText          : residential.greyDarker,
    systemStatusButtonBorderHover   : residential.grey,

    selectButton                    : residential.greyLightest,
    selectButtonHover               : residential.greyLighter,

    // icons

    actionableIcon                  : residential.greyDarker,
    actionableIconDisabled          : residential.greyLighter,
    actionableIconHover             : residential.greyDarkest,
    clearIcon                       : residential.grey,
    clearIconHover                  : residential.greyDark,
    customerPin                     : residential.yellowDark,
    customerPinUnpinned             : residential.greyLight,
    deselectedLegendCheckmarkHover  : residential.greyDarker,
    deselectedLegendCheckmark       : residential.greyLight,
    dropdown                        : residential.greyDark,
    emptyNote                       : residential.greyDark,
    filledNote                      : residential.yellowDark,
    filterIcon                      : residential.blue,
    EditIcon                        : residential.blueDark,
    questionMarkIcon                : residential.blue,
    selectedLegendCheckmark         : residential.greyDarkest,
    notificationDot                 : residential.red,

    destructiveIcon                 : residential.redLighter,
    destructiveIconHover            : residential.red,

    // rth chart

    infoPanelHeaderBackground       : residential.greyLighter,
    infoPanelContentBackground      : residential.greyLightest,

    awairCo2                        : rthChart.teal,
    awairHumidity                   : rthChart.brown,
    awairPm25                       : rthChart.green,
    awairTemp                       : rthChart.magenta,
    awairVoc                        : rthChart.purple,

    indoorUnitActualAirflow         : residential.yellow,
    legendOutdoor                   : residential.yellowDarker,
    legendOdLiquidPressure          : residential.purple,
    legendOdLiquidTemp              : residential.orange,
    legendOdSubCool                 : residential.greenDark,
    legendOdSuctionPressure         : residential.bluegreen,
    legendOdSuctionTemperature      : residential.purpleDark,
    legendOdSuperheat               : residential.redDark,
    legendRelieving                 : residential.greyDark,
    legendIndoor                    : rthChart.blue,
    legendOdEevStepColor            : rthChart.lavenderBlue,
    legendOdCoilTemp                : residential.redPink,

    legendIdEevStepColor            : rthChart.purpleLightest,
    legendIdSupplyAirTempColor      : rthChart.purpleLighter,
    legendIdReturnAirTempColor      : rthChart.purpleLight,
    legendIdCoilTemp                : rthChart.paleRed,
    legendGasTemp                   : rthChart.red,
    legendIdSuperHeat               : rthChart.darkRed,
    pillCooling                     : residential.blueDark,
    pillHeating                     : residential.redDark,

    // consumer rth chart
    cool                            : consumerRTHChart.cool,
    coolStage2                      : consumerRTHChart.coolStage2,
    heat                            : consumerRTHChart.heat,
    heatStage2                      : consumerRTHChart.heatStage2,
    heatStage3                      : consumerRTHChart.heatStage3,
    heatAux                         : consumerRTHChart.heatAux,
    heatAuxStage2                   : consumerRTHChart.heatAuxStage2,
    heatAuxStage3                   : consumerRTHChart.heatAuxStage3,
    hoverBox                        : consumerRTHChart.hoverBox,
    hoverBoxBackground              : consumerRTHChart.hoverBoxBackground,
    chartBackground                 : consumerRTHChart.chartBackground,

    // device metric graph

    deviceMetricRedStatus     : residential.red,
    deviceMetricYellowStatus  : residential.yellowDark,
    deviceMetricGreenStatus   : residential.green,

    // Thermostat Box
    thermostatBoxBackground         : residential.blueDarkest,
    thermostatBoxHeatSetpoint       : residential.red,
    thermostatBoxCoolSetpoint       : residential.blue,
    thermostatBoxForeground         : residential.white,
    iconDropletBackground           : residential.blueDark,
    DashboardThermostatBackground   : residential.greyLighter,
    DashboardThermostatText         : residential.greyDarker,
    DashboardThermostatBorder       : residential.blueTranslucent,
    DashboardThermostatSetpont      : residential.white,

    // Damper
    damperFloWDirection             : residential.greyDark,
    damperBackground                : residential.greyLighter,

    // Sys Config
    editText                        : residential.black,
    deleteIcon                      : residential.greyDarkest,

    // alerts
    criticalAlert                   : residential.red,
    majorAlert                      : residential.yellow,
    majorSdaAlert                   : residential.purpleDarker,
    minorAlert                      : residential.green,
    minorSdaAlert                   : residential.purpleDarker,
    dirtyFilter                     : residential.white,

    criticalAlertInactive           : residential.dimRed,
    majorAlertInactive              : residential.dimYellow,
    majorSdaAlertInactive           : residential.dimPurple,
    minorAlertInactive              : residential.dimGreen,
    minorSdaAlertInactive           : residential.dimPurple,

    // device metric
    needsAttention                  : residential.red,
    notOptimal                      : residential.yellow,
    good                            : residential.green,

    // connection status
    connected                       : residential.green,
    disconnected                    : residential.red,

    // service contract
    serviceContractActive           : residential.blue,
    serviceContractExpired          : residential.greyDarkest,
    serviceContractNone             : residential.dimGrey,

    // Device Tabs
    inactiveTabBackground           : residential.greyLighter,
    activeTabBackground             : residential.greyLightest,
    activeTabHighlight              : Brand.isCommercial() ? commercial.grayDark : residential.blue,
    inactiveTabText                 : residential.grey,
    // Customer Adaption
    customerAdaptionOptedIn             : residential.green,
    customerAdaptionSomeSystemsOptedIn  : residential.greenDark,
    customerAdaptionOptedOut            : residential.blue,
    customerAdaptionNoSystems           : residential.greyLighter,

    // Remote Config List
    remoteConfigPanelHeaderBackground : residential.yellow,
    remoteConfigPanelHeaderText       : residential.greyDarkest,

    pageTitle                         : residential.dimGray,
    cardTitle                         : residential.dimGray,
    cardDescription                   : residential.dimGray,
    statusText                        : residential.dimGray,
    searchPlaceholder                 : residential.dimGrayLight,
    username                          : residential.dimGray,

    // Remote access Link Systems
    modalTitle                        : residential.dimGray,
    modalText                         : residential.dimGray,
    modalGridBackground               : residential.dimGrayLighter,

    // Help videos icon
    helpVideoLink                     : residential.blue,
    iconBackground                    : residential.white,

    // Maintenanace Trends
    trendHeaderBackground              : residential.greyLight,
    maintenancePanelBackground          : residential.dimGrayLighter,
    maintenanceChartBackground          : residential.dimGrayLighter,
    maintenancePlotBandBackground       : residential.dimGrayLightest,
    maintenanceChartText                : residential.greyDarkest,
    maintenanceTrendHoverBoxBackground : residential.white,
    maintenanceTrendHoverBoxBoarder    : residential.greyDarkest,

    // AlertLookup
    alertDescriptions                  : residential.greyDark,
    alertDescriptionsHover             : residential.greyDarkest,
    alertLookupButton                  : residential.blue,
    alertLookupButtonHover             : residential.blueDarker,

    // Advance Customer Filter
    customerFilterBorder               : residential.greyDark,
    customerFilterGroup                : residential.dimGrayLighter,
    customerFilterText                 : residential.greyDarker,
    customerFilterHoverBorder          : residential.blueDark,
    customerFilterHoverBackground      : residential.blueLight,
    customerFilterSelected             : residential.blueDark,
    customerFilterSelectedBorder       : residential.blueDark,
    customerFilterSelectedBackground   : residential.blueLighter,
    customerFilterButtonText           : residential.white,

    // Admin Banner
    adminBannerProduction              : residential.red,
    adminBannerDevelopment             : residential.cornflowerBlue,
    adminBannerStaging                 : residential.yellowOrange,
    adminBannerQA                      : rthChart.teal,

    // system dashboard
    LastUpdatedText                     : residential.dimGray,
    dashboardBackground                 : residential.greyLighter,
    dashboardCardBackground             : residential.white,
    dashboardCardHoverBorder            : residential.blue,
    disabledTextColor                   : residential.greyLight,
    dashboardSecondaryText              : residential.greyDarker,

    emailBackground                     : residential.cloudGray
  }
}

let componentColors

export const color = (componentName) => {
  if (!componentColors) {
    componentColors = loadComponentColors()
  }

  if (!componentColors[componentName]) {
    throw new Error(`Component color "${componentName}" does not exist.`)
  }

  return componentColors[componentName]
}

export const hexToRgb = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
